import React, { useMemo, useRef } from 'react';
import { shallowEqual, useSelector } from 'umi';
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { formatFilterValues, otherParamsVal } from '@/pages/dashboard/utils/utils';
import moment from 'moment';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import { getApiData } from '@/pages/dashboard/services/common';
import { ICountryComparisonServiceProps, IRequestParams } from './type';

const SalesBreakdownCardService: React.FC<ICountryComparisonServiceProps> = (props) => {
  const {
    style,
    className,
    type,
    config,
    children,
    data: propsData,
    selectedMetrics,
    ...restProps
  } = props || {};

  const { api, dataKey, refreshKey = '', interval, breakdownType = '', metrics, otherParams: otherParamsData } = config || {};

  const { gameCode } = useSelector((state: any) => ({
    gameCode: state?.dashboard?.currentGame?.game_code,
  }), shallowEqual);

  const data = useMemo(() => ({
    gameCode,
    ...(propsData || {})
  }), [gameCode, propsData]);

  // 请求版本号，用于解决快速切换时的竞态问题
  const requestVersionRef = useRef<number>(0);

  const apiRequest = (params: any) => {
    // 每次发起新请求时递增版本号
    requestVersionRef.current += 1;
    const currentVersion = requestVersionRef.current;

    return getApiData(api, params).then((data: any) => {
      // 版本号不匹配说明有更新的请求已发出，丢弃旧结果
      if (currentVersion !== requestVersionRef.current) {
        return new Promise(() => {});
      }
      return data;
    }).catch((e: any) => {
      // 版本号不匹配时也不触发错误处理
      if (currentVersion !== requestVersionRef.current) {
        return new Promise(() => {});
      }
      console.log(e);
    });
  };

  const beforeRequest = (data: any) => {
    const { filterVal, gameCode, metric, selectedRegion } = data;
    const { timeType, rangeTime, ...filters } = filterVal || {};
    const filtersVal = formatFilterValues(filters);
    const otherParams = otherParamsVal(otherParamsData, filtersVal) || {};

    let startDate;
    let endDate;

    if (dataKey === 'trends_data' || dataKey === 'discount_trends_data') {
      startDate = 'start_date';
      endDate = 'end_date';
    } else {
      startDate = 'start_time';
      endDate = 'end_time';
    }

    const baseParams: IRequestParams = {
      game_code: gameCode,
      [startDate]: moment(rangeTime?.[0]).format('YYYY-MM-DD'),
      [endDate]: moment(rangeTime?.[1]).format('YYYY-MM-DD'),
      period: PERIODTIMEOBJ[timeType],
      
      filters: filtersVal,
    };

    if (dataKey === 'trends_data') {
      const params: IRequestParams = {
        ...baseParams,
        metrics,
      };
      return { ...params, ...(otherParams || {}) };
    }

    if (dataKey === 'breakdown_data') {
      const params: IRequestParams = {
        ...baseParams,
        by_type: breakdownType,
        metric: metric || '',
      };

      return { ...params, ...(otherParams || {}) };
    }

    if (dataKey === 'discount_trends_data') {
      const params: IRequestParams = {
        ...baseParams,
        filters: {
          ...baseParams.filters,
          market_list: [selectedRegion]
        }
      };
      return { ...params, ...(otherParams || {}) };
    }

    return { ...baseParams, ...(otherParams || {}) };
  };

  const trackData: string[] = [
    'gameCode', 
    dataKey === 'breakdown_data' && 'metric',
    dataKey === 'discount_trends_data' && 'selectedRegion',
  ].filter(Boolean) as string[];

  return (
    <div className={className} style={style}>
      {/* @ts-ignore */}
      <Service
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        api={apiRequest}
        data={data}
        dataKey={dataKey}
        refreshKey={refreshKey}
        interval={interval}
        beforeRequest={beforeRequest}
        trackData={trackData}
      >
        {
          React.Children.map(children, (child: any) => React.cloneElement(
            child,
            {
              ...restProps,
              selectedMetrics,
              state: {},
            },
          ))
        }
      </Service>
    </div>
  );
};

export default SalesBreakdownCardService;